import React from 'react'
import styled, { useTheme } from 'styled-components'
import { LinearProgress } from '@mui/material'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import UseAnimations from 'react-useanimations'
import loading from 'react-useanimations/lib/loading'
import { displayDuration } from 'utils/displayDuration'
import { useTranslation } from 'react-i18next'
import { ClassicStakeConfig, StakeCounterType, useStakeTimestampCounter } from '../classicStake.logic'

const Progress = styled(LinearProgress)`
  width: 100%;
  border-radius: 12px;
  &.MuiLinearProgress-root {
    color: ${({ theme }) => theme.colors.gray10};
    background: ${({ theme }) => theme.colors.gray8};
    .MuiLinearProgress-bar {
      background: ${({ theme }) => theme.colors.purple};
      border-radius: 12px;
    }
  }
`

const StyledText = styled(Text)`
  white-space: nowrap;
  ${({ theme }) => theme.mediaQueries.mobile} {
    letter-spacing: -0.4px;
  }
`

interface StakeCounterProps {
  stake: ClassicStakeConfig
}

const StakeCounter: React.FC<StakeCounterProps> = ({ stake }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { counterLoading, eventType, progress, totalTime } = useStakeTimestampCounter(stake)
  const formattedTime = totalTime > 0 ? displayDuration({ time: totalTime }) : '--:--:--'

  const getCounterTitle = () => {
    switch (eventType) {
      case StakeCounterType.STARTS:
        return t('stakingPanel.stakeStartsIn')
      case StakeCounterType.ACTIVE:
        return t('stakingPanel.stakeEndsIn')
      default:
        return t('stakingPanel.stakeEnded')
    }
  }

  const isCounterVisible = eventType === StakeCounterType.ACTIVE

  return (
    <Flex flexDirection="column" fullWidth flexGrow={1} gap="7px">
      <Flex fullWidth justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <StyledText variant={TextVariant.BODY_SMALL} color={colors.gray6}>
          {getCounterTitle()}
        </StyledText>
        {counterLoading ? (
          <UseAnimations animation={loading} strokeColor={colors.gray6} size={16} />
        ) : (
          <StyledText variant={TextVariant.BODY_DEFAULT_BOLD}>{formattedTime}</StyledText>
        )}
      </Flex>
      {isCounterVisible && (
        <Flex fullWidth>
          <Progress variant="determinate" value={progress} />
        </Flex>
      )}
    </Flex>
  )
}

export default StakeCounter
